import {
  Stack,
  ToggleButtonGroup,
  Typography,
  Divider,
  ToggleButton,
} from '@mui/material';
import { type InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';

import type { BackupVault } from '@/data/vaults/backupVault';
import { TIME_FORMAT, type Dayjs } from '@/utils/dayjs';

import { ContinuousSnapshotRow } from './continuousSnapshotRow';
import { SnapshotsEmptyState } from './snapshotsRows';

export interface ContinuousSnapshot {
  start: Dayjs;
  end: Dayjs;
  vault?: BackupVault;
}

export const ContinuousSnapshots = ({
  resource,
  selectedDate,
  dateRanges,
}: {
  resource: InventoryResource;
  selectedDate: Dayjs;
  dateRanges: ContinuousSnapshot[];
}) => {
  const [timeType, setTimeType] = useState<
    'latest-available' | 'specific-time'
  >('latest-available');

  if (dateRanges.length === 0) {
    return <SnapshotsEmptyState />;
  }

  const isLastDayInRange = dateRanges.every((dateRange) =>
    dateRange.end.isSame(selectedDate, 'day')
  );

  const earliestInRanges = dateRanges.reduce((min, dateRange) => {
    return dateRange.start.isBefore(min) ? dateRange.start : min;
  }, dateRanges[0].start);
  const latestInRanges = dateRanges.reduce((max, dateRange) => {
    return dateRange.end.isAfter(max) ? dateRange.end : max;
  }, dateRanges[0].end);
  const startTime = earliestInRanges.isBefore(selectedDate.startOf('day'))
    ? selectedDate.startOf('day')
    : earliestInRanges;
  const endTime = latestInRanges.isAfter(selectedDate.endOf('day'))
    ? selectedDate.endOf('day')
    : latestInRanges;

  return (
    <Stack
      gap='24px'
      data-testid='continuous-snapshot-row'
      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
      className='py-[24px] px-[40px] w-full'
    >
      <ContinuousSnapshotHeader
        startTime={startTime}
        endTime={endTime}
        timeType={timeType}
        setTimeType={setTimeType}
        isLastDayInRanges={isLastDayInRange}
      />
      <Stack gap='12px'>
        {dateRanges.map((dateRange) => (
          <ContinuousSnapshotRow
            key={dateRange.vault?.id || 'source'}
            resource={resource}
            selectedDate={selectedDate}
            dateRange={dateRange}
            timeType={timeType}
            vault={dateRange.vault}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const ContinuousSnapshotHeader = ({
  startTime,
  endTime,
  timeType,
  setTimeType,
  isLastDayInRanges,
}: {
  startTime: Dayjs;
  endTime: Dayjs;
  timeType: 'latest-available' | 'specific-time';
  setTimeType: (timeType: 'latest-available' | 'specific-time') => void;
  isLastDayInRanges: boolean;
}) => {
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography variant='body1'>{`Available recovery points between ${startTime.format(TIME_FORMAT)} and ${endTime.format(TIME_FORMAT)} UTC`}</Typography>
      <ToggleButtonGroup
        exclusive
        value={timeType}
        onChange={(_, value) => {
          if (value === null) {
            return;
          }
          setTimeType(value);
        }}
      >
        <ToggleButton value={'latest-available'}>
          {isLastDayInRanges ? 'Latest available' : 'Latest in this day'}
        </ToggleButton>
        <ToggleButton value={'specific-time'}>Specific time</ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );
};
